import styled from "@emotion/styled";
import { FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Alert,
  Button,
  Heading,
  IPickerListItem,
  Modal,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import { ALL_TIME_KEY, ImportTicketsDateRangeSelect } from "components/ImportTicketsDateRangeSelect";
import Flex from "components/layout/Flex";
import { SubscriptionPhase } from "constants/subscription-phase";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { getSubscriptionPhase } from "helpers/subscription";
import { getNonViewersCount } from "store/entities/agents";
import { getBilling } from "store/entities/billing";
import {
  IntegrationsActions,
  selectIsManagedByShopify,
  selectIsShopifyTrial,
  selectLiveChatSelectiveImportDiff,
} from "store/entities/integrations";
import { selectManagedByPartner } from "store/entities/license";
import { selectCurrentSubscription, selectSeatsCount } from "store/entities/subscriptions";
import { withVar } from "themes/helpers";

interface IProps {
  onClose: () => void;
}

const FullImportModal: FC<IProps> = ({ onClose }) => {
  const dispatch = useDispatch();

  const billing = useSelector(getBilling);
  const subscription = useSelector(selectCurrentSubscription);
  const seatsCount = useSelector(selectSeatsCount);
  const nonViewersCount = useSelector(getNonViewersCount);
  const importDiff = useSelector(selectLiveChatSelectiveImportDiff);
  const isHandledByShopify = useSelector(selectIsManagedByShopify);
  const isShopifyTrial = useSelector(selectIsShopifyTrial);
  const isManagedByPartner = useSelector(selectManagedByPartner);

  const [selectedDateRange, setSelectedDateRange] = useState<IPickerListItem | null>(null);

  const subscriptionPhase = getSubscriptionPhase(subscription, billing);
  const agentsToImport = importDiff?.agents?.create || 0;
  const seatsToAdd = agentsToImport - (seatsCount - nonViewersCount);
  const shouldShowDisclaimer =
    (subscriptionPhase === SubscriptionPhase.Paid ||
      subscriptionPhase === SubscriptionPhase.PaidNoCC ||
      (isHandledByShopify && !isShopifyTrial) ||
      isManagedByPartner) &&
    seatsToAdd > 0;

  const handleStartImport = (): void => {
    if (!selectedDateRange) {
      return;
    }
    dispatch(
      IntegrationsActions.startLiveChatImport({
        ...(selectedDateRange.key === ALL_TIME_KEY ? {} : { importDataFrom: selectedDateRange.key }),
      })
    );
    trackEngagement("LC integration: Import data", EventPlace.AppDetails, {
      importedData: "full",
      firstTime: true,
      eventPlace: "integrationSettings",
      timeRange: selectedDateRange.key,
    });
  };

  const handleImportClick = (): void => {
    handleStartImport();
    onClose();
  };

  return (
    <Modal
      css={{ width: "600px" }}
      closeOnOverlayPress={false}
      onClose={onClose}
      heading="Import LiveChat tickets"
      footer={
        <Flex gap={withVar(SpacingToken.Spacing2)}>
          <Button kind="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" onClick={handleImportClick} disabled={!selectedDateRange}>
            Import
          </Button>
        </Flex>
      }
    >
      <Flex gap={withVar(SpacingToken.Spacing2)} alignItems="center" paddingBottom={withVar(SpacingToken.Spacing6)}>
        <Heading size="xs">Import tickets from</Heading>
        <ImportTicketsDateRangeSelect selectedDateRange={selectedDateRange} onDateRangeSelect={setSelectedDateRange} />
      </Flex>
      <Alert kind="info">
        <Text bold>After importing tickets, you&apos;ll get these benefits:</Text>
        <FullImportInfoList>
          <li>HelpDesk will handle all tickets instead of LiveChat.</li>
          <li>Ticket form messages will go to HelpDesk.</li>
          <li>Emails sent to LiveChat will go to HelpDesk.</li>
          <li>The General group in LiveChat, with all its members, will become the default team in HelpDesk.</li>
        </FullImportInfoList>
        <Text>
          <br />
          To keep LiveChat ticket data consistent, the following will also be imported: <b>Agents</b>, <b>Groups</b>,{" "}
          <b>canned responses</b>, and <b>tags</b>.
        </Text>
        <Text>
          <br />
          This change is <b>irreversible</b> and will be done for the entire account.
        </Text>
        {shouldShowDisclaimer && (
          <Text data-testid="subscription-update-info">
            <br />
            Imported LiveChat users will keep their roles in HelpDesk and <b>be billed per the chosen HelpDesk plan</b>.
          </Text>
        )}
      </Alert>
    </Modal>
  );
};

const FullImportInfoList = styled.ul`
  list-style: disc;
  padding-left: ${withVar(SpacingToken.Spacing5)};
`;

export { FullImportModal, type IProps as IFullImportModalProps };
